import {
  Document,
  DocumentCategory,
  DocumentType,
  FilingType,
  LetterType,
  PresentationType,
  TranscriptType,
} from '../../../store/document/types';

/**
 * Helper class for managing document categories and types in the file explorer
 */
class DocumentCategoryUtils {
  /**
   * Get all non-empty document categories
   */
  static getNonEmptyCategories(documents: Document[]): DocumentCategory[] {
    if (!documents || documents.length === 0) return [];

    const categories = new Set<DocumentCategory>();
    documents.forEach((doc) => {
      if (doc.category) {
        categories.add(doc.category);
      }
    });

    return Array.from(categories);
  }

  /**
   * Get all document types for a specific category
   */
  static getDocumentTypesByCategory(
    documents: Document[],
    category: DocumentCategory,
  ): DocumentType[] {
    if (!documents || documents.length === 0) return [];

    const types = new Set<DocumentType>();
    // skip exhibits and earnings releases since we group those with 8-ks
    documents.forEach((doc) => {
      if (
        doc.category === category &&
        doc.type &&
        doc.type !== FilingType.EXHIBIT &&
        doc.type !== FilingType.EARNINGS_RELEASE
      ) {
        types.add(doc.type);
      }
    });

    return Array.from(types);
  }

  /**
   * Get all documents of a specific type
   */
  static getDocumentsByType(
    documents: Document[],
    category: DocumentCategory,
    type: DocumentType,
  ): Document[] {
    if (!documents || documents.length === 0) return [];

    return documents.filter((doc) => doc.type === type && doc.category === category);
  }

  /**
   * Format category or document type for display
   */
  static formatCategoryName(name: DocumentCategory): string {
    switch (name) {
      case DocumentCategory.FILING:
        return 'Filings';
      case DocumentCategory.TRANSCRIPT:
        return 'Transcripts';
      case DocumentCategory.LETTER:
        return 'Letters';
      case DocumentCategory.PRESENTATION:
        return 'Presentations';
      default:
        return name;
    }
  }

  static formatDocumentTypeName(name: DocumentType): string {
    switch (name) {
      case FilingType.EIGHT_K:
        return '8-K';
      case FilingType.TEN_K:
        return '10-K';
      case FilingType.TEN_Q:
        return '10-Q';
      case FilingType.TWENTY_F:
        return '20-F';
      case FilingType.EXHIBIT:
        return 'Exhibit';
      case FilingType.EARNINGS_RELEASE:
        return 'Earnings Release';
      case FilingType.SIX_K:
        return '6-K';
      case TranscriptType.EARNINGS_CALL:
        return 'Earnings Calls';
      case TranscriptType.SPECIAL:
        return 'Special';
      case LetterType.SHAREHOLDER_LETTER:
        return 'Shareholder Letters';
      case PresentationType.INVESTOR:
        return 'Investor Presentations';
      default:
        return name;
    }
  }
}

export default DocumentCategoryUtils;
